
    // IMPORTS
    import { defineComponent, computed, ref, onMounted, inject, reactive } from 'vue'
    import router from '@/router'
    import { useShoppingCartStore } from '@/store/shoppingCart'
    import { useBrowserStateStore } from '@/store/browserState'

    import { RouterLink } from 'vue-router'
    import { Card, CardHeader, CardTitle, CardBody } from '@progress/kendo-vue-layout'
    import { Button } from '@progress/kendo-vue-buttons'
    import LoaderGuard from '@/components/common/loader-guard.vue'
    import MyIcon from '@/components/common/my-icon.vue'
    import CountrySelector from '@/components/common/country-selector.vue'
    import InputForm from './input-form.vue'
    import InputField from './input-field.vue'
    import AddressSelector from './address-selector.vue'

    import { buildMaterialLocalization, waitUntil } from '@/util'
    import { IDeliveryAddressDTO } from '@/types/shoppingCart'

    export default defineComponent({
        name: 'shoppingCartCheckout',

        components: {
            LoaderGuard,
            MyIcon,
            CountrySelector,
            InputForm,
            InputField,
            Card,
            CardHeader,
            CardBody,
            CardTitle,
            'k-button': Button,
            AddressSelector,
            RouterLink
        },

        setup() {
            /********************************************************************************************************************
             PRIVATE DATA
             ********************************************************************************************************************/
            const shoppingCartStore = useShoppingCartStore()
            const browserState = useBrowserStateStore()

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const orderData = reactive(shoppingCartStore.orderData)

            // Indicates whether the shopping cart is currently being committed
            const isCommitting = ref(false)

            // Indicates whether a shopping cart commit has just succeeded
            const showCommitSuccess = ref(false)

            // An optional text comment to append to the shopping cart commit
            const commitComment = ref('')

            const selectedCountry = ref('')

            const comment = ref('')

            const doValidate = ref(false)

            const publicData = {
                orderData,
                isCommitting,
                showCommitSuccess,
                commitComment,
                selectedCountry,
                comment,
                doValidate
            }

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const hasLoaded = computed(() => shoppingCart.value != null)
            const isSmallScreen = computed(() => browserState.isSmallScreen)
            const shoppingCart = computed(() => shoppingCartStore.shoppingCart)
            const shoppingCartItems = computed(() => shoppingCart.value?.items ?? [])
            const emptyShoppingCart = computed(() => shoppingCartItems.value.length == 0)

            const kendoForm = inject('kendoForm', {})

            const computedData = {
                hasLoaded,
                isSmallScreen,
                shoppingCartItems,
                emptyShoppingCart,
                kendoForm
            }

            /********************************************************************************************************************
             PUBLIC METHODS AND EVENT HANDLERS
             ********************************************************************************************************************/
            const { localize } = buildMaterialLocalization()

            function onBack() {
                //shoppingCartStore.setOrderData(orderData)
                router.replace({ name: 'shopping-cart-content' })
            }

            function onCommitOrder() {
                isCommitting.value = true

                shoppingCartStore.commit(orderData)
                    .then(() => showCommitSuccess.value = true)
                    .finally(() => { isCommitting.value = false })
            }

            function myValidator(value: string): string | null {
                if (value != 'claus') return 'must be claus!'
                return null
            }

            function onSelectedAddress(address: IDeliveryAddressDTO) {
                orderData.deliveryAddress = {
                    ...address,
                    attention: orderData.deliveryAddress.attention // keep existing "attention" entry
                }
            }

            const publicMethods = {
                localize,
                onBack,
                onCommitOrder,
                myValidator,
                onSelectedAddress
            }

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/

            /********************************************************************************************************************
             LIFE-CYCLE HOOKS
             ********************************************************************************************************************/
            onMounted(() => {
                waitUntil(() => hasLoaded.value)
                    .then(() => {
                        if (emptyShoppingCart.value === true) {
                            router.replace({ name: 'shopping-cart-content' })
                        }
                    })
            })

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...publicData, ...computedData, ...publicMethods }
        }
    });
